import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Phone, 
  DollarSign, 
  Video,
  ArrowRight,
  Clock,
  FileDown,
  Radio,
  Ticket,
  Lock,
  Loader2,
  CheckCircle,
  ExternalLink,
  Mail,
  Navigation,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { SkipControls, canSkip } from '../components/SkipControls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Interactive location component with embedded Google Maps - full width
const FullWidthMap = ({ location, eventId, mapUrl }) => {
  const [showMap, setShowMap] = useState(false);
  const encodedLocation = encodeURIComponent(location);
  const mapsSearchUrl = mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
  
  const getEmbedUrl = () => {
    if (mapUrl) {
      const placeMatch = mapUrl.match(/place\/([^/]+)/);
      const queryMatch = mapUrl.match(/[?&]q=([^&]+)/);
      const coordMatch = mapUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (placeMatch) return `https://www.google.com/maps?q=${placeMatch[1]}&output=embed&t=k`;
      if (queryMatch) return `https://www.google.com/maps?q=${queryMatch[1]}&output=embed&t=k`;
      if (coordMatch) return `https://www.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&output=embed&t=k`;
    }
    return `https://www.google.com/maps?q=${encodedLocation}&output=embed&t=k`;
  };

  return (
    <div className="mb-6">
      <button 
        className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors mb-2"
        onClick={() => setShowMap(!showMap)}
        data-testid={`event-location-${eventId}`}
      >
        <Navigation className="w-4 h-4" />
        {showMap ? 'Hide Map' : 'View on Map'}
      </button>
      
      {showMap && (
        <div className="rounded-xl overflow-hidden border border-border shadow-lg animate-in slide-in-from-top-2 duration-300" data-testid={`event-map-${eventId}`}>
          <iframe
            src={getEmbedUrl()}
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`Map: ${location}`}
            className="w-full"
          />
          <div className="bg-card px-4 py-3 flex items-center justify-between border-t">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary" />
              <span>{location}</span>
            </div>
            <a
              href={mapsSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline whitespace-nowrap"
              data-testid={`event-directions-${eventId}`}
            >
              <Navigation className="w-4 h-4" />
              Get Directions
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

const getEmbedUrlFromInput = (input) => {
  if (!input) return null;
  const trimmed = input.trim();
  // If the admin pasted the FULL <iframe> HTML embed code (Vimeo's copy-paste
  // default), pull out the src attribute so we can normalize it and apply our
  // safe playback params below. Without this we'd fall back to raw HTML render
  // which strips autoplay/muted/playsinline — that was the "10s reload" bug.
  let url = trimmed;
  if (trimmed.startsWith('<')) {
    const srcMatch = trimmed.match(/<iframe[^>]*\bsrc=["']([^"']+)["']/i);
    if (!srcMatch) return null; // no iframe found; caller falls back to dangerouslySetInnerHTML
    url = srcMatch[1];
  }
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&modestbranding=1`;
  // Vimeo Live Event embed params:
  //   autoplay=1&muted=1 is the ONLY combo modern browsers allow. Unmuted
  //   autoplay triggers a retry loop that manifests as periodic freeze/resume
  //   glitching on live streams. Users can unmute via the player controls.
  //   playsinline=1 is CRITICAL for iOS Safari — without it, iOS tries to hoist
  //   the video into system fullscreen on every play, then aborts because we're
  //   embedded → Vimeo retries → looks like a ~10s glitch loop on iPhones.
  //   dnt=1 (do-not-track) is dropped — it can prevent Vimeo's CDN
  //   optimizations from kicking in and worsens buffering.
  const VIMEO_PARAMS = 'title=0&byline=0&portrait=0&badge=0&autopause=0&autoplay=1&muted=1&playsinline=1';
  // Event with token OR /embed/interaction OR /embed/<anything>.
  // NOTE: We STRIP a trailing "/interaction" segment. That endpoint is Vimeo's
  // interactive-event player and it waits for a chat/poll config from the
  // parent frame via postMessage. Since we don't send that config, Vimeo
  // throws PlaybackError after ~400ms and then re-tries every ~10s — exactly
  // the "refreshes every 10 seconds" symptom on live events. Plain /embed
  // just plays the video, which is what we want.
  const vimeoEventTokenMatch = url.match(/vimeo\.com\/event\/(\d+)\/embed\/([A-Za-z0-9_-]+)/);
  if (vimeoEventTokenMatch) {
    const tokenPart = vimeoEventTokenMatch[2];
    if (tokenPart === 'interaction') {
      return `https://vimeo.com/event/${vimeoEventTokenMatch[1]}/embed?${VIMEO_PARAMS}`;
    }
    return `https://vimeo.com/event/${vimeoEventTokenMatch[1]}/embed/${tokenPart}?${VIMEO_PARAMS}`;
  }
  const vimeoEventMatch = url.match(/vimeo\.com\/event\/(\d+)/);
  if (vimeoEventMatch) return `https://vimeo.com/event/${vimeoEventMatch[1]}/embed?${VIMEO_PARAMS}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?${VIMEO_PARAMS}`;
  // Already a player URL — respect it but append our params if missing
  if (url.startsWith('http') && url.includes('vimeo.com') && !url.includes('autoplay=')) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${VIMEO_PARAMS}`;
  }
  if (url.startsWith('http')) return url;
  return null;
};

const getVimeoDirectUrl = (input) => {
  if (!input) return null;
  const trimmed = input.trim();
  // Extract from plain URL
  const eventMatch = trimmed.match(/vimeo\.com\/event\/(\d+)/);
  if (eventMatch) return `https://vimeo.com/event/${eventMatch[1]}`;
  const videoMatch = trimmed.match(/vimeo\.com\/(\d+)/);
  if (videoMatch) return `https://vimeo.com/${videoMatch[1]}`;
  // Extract from embed HTML
  const srcMatch = trimmed.match(/src=["']?([^"'\s>]*vimeo\.com[^"'\s>]*)/);
  if (srcMatch) {
    const embedUrl = srcMatch[1];
    const eidMatch = embedUrl.match(/vimeo\.com\/event\/(\d+)/);
    if (eidMatch) return `https://vimeo.com/event/${eidMatch[1]}`;
    const vidMatch = embedUrl.match(/vimeo\.com\/video\/(\d+)/);
    if (vidMatch) return `https://vimeo.com/${vidMatch[1]}`;
  }
  return null;
};

const getYouTubeDirectUrl = (input) => {
  if (!input) return null;
  const match = input.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s"']+)/);
  if (match) return `https://www.youtube.com/watch?v=${match[1]}`;
  return null;
};

// Memoized iframe embed. Only re-renders when the URL or title actually
// change — critical for Vimeo Live, which restarts from black if the iframe
// is unmounted/remounted by React reconciliation (e.g., after a poll refresh).
//
// Uses the padding-bottom aspect-ratio pattern (56.25% = 16/9) instead of
// Tailwind's `aspect-video`. Some mobile browsers (notably Samsung Internet
// on certain models) apply a default iframe height that overrides the
// CSS `aspect-ratio` property, chopping the video off. Padding-bottom on
// the wrapper is universally supported and always correct.
const StableEmbedFrame = React.memo(function StableEmbedFrame({ embedUrl, title }) {
  return (
    <div
      className="relative w-full h-0 overflow-hidden"
      style={{ paddingBottom: '56.25%' }}
    >
      <iframe
        src={embedUrl}
        className="absolute inset-0 w-full h-full border-0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write"
        allowFullScreen
        webkitallowfullscreen="true"
        mozallowfullscreen="true"
        title={title || 'Video'}
      />
    </div>
  );
});

// Embed + ±5/10s skip buttons (Vimeo players only — they expose a seek API).
const EmbedWithSkip = ({ children, embedUrl }) => {
  const ref = React.useRef(null);
  return (
    <div ref={ref}>
      {children}
      {canSkip('iframe', embedUrl) && (
        <SkipControls getTarget={() => ref.current?.querySelector('iframe')} className="py-3 bg-black" testidPrefix="event-skip" />
      )}
    </div>
  );
};

const renderEmbed = (embedCode, title) => {
  if (!embedCode) return null;
  const val = embedCode.trim();
  const embedUrl = getEmbedUrlFromInput(val);

  return (
    <EmbedWithSkip embedUrl={embedUrl}>
      {embedUrl ? (
        // key = embed URL so React NEVER re-mounts the iframe unless the admin
        // actually changes the stream. Vimeo Live is very sensitive: every
        // remount restarts the player and buffers 5-10s on mobile — that was
        // the "glitching every 10s" symptom.
        <StableEmbedFrame key={embedUrl} embedUrl={embedUrl} title={title} />
      ) : (
        <div dangerouslySetInnerHTML={{ __html: val }} />
      )}
    </EmbedWithSkip>
  );
};

const EventsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [liveStream, setLiveStream] = useState({ embed_code: '', is_live: false, title: '', subscriber_price: 0, non_subscriber_price: 0 });
  const [pastEvents, setPastEvents] = useState([]);
  const [tickets, setTickets] = useState({}); // { event_key: true/false }
  const [purchasingTicket, setPurchasingTicket] = useState(null);
  const [checkingTickets, setCheckingTickets] = useState(true);
  const [selectedPastEvent, setSelectedPastEvent] = useState(null);

  // Check if user is a subscriber (trial users are NOT treated as subscribers for paid content)
  const isTrial = user?.subscription_status === 'trial';
  const isSubscriber = user?.subscription_status === 'active' && !isTrial;
  
  // State for upcoming events from API
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [liveRes, pastRes, upcomingRes] = await Promise.all([
          axios.get(`${API_URL}/api/live-stream`),
          axios.get(`${API_URL}/api/past-events`),
          axios.get(`${API_URL}/api/events/upcoming`)
        ]);
        setLiveStream(prev => {
          // Avoid unnecessary state churn: only take the new object if any
          // field the iframe cares about actually changed. Prevents a needless
          // re-render (and Vimeo Live iframe restart) every 30 s.
          const next = liveRes.data || {};
          if (
            prev.is_live === next.is_live &&
            prev.embed_code === next.embed_code &&
            prev.title === next.title
          ) {
            return prev;
          }
          return next;
        });
        setPastEvents(pastRes.data.events || []);
        setUpcomingEvents(upcomingRes.data.events || []);
      } catch (err) {
        console.warn('Failed to fetch events:', err);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check ticket status for all events
  useEffect(() => {
    const checkTickets = async () => {
      if (!user) {
        setCheckingTickets(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        // Check live stream ticket
        const liveTicket = await axios.get(`${API_URL}/api/events/ticket-status?event_key=live_stream`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const ticketMap = { live_stream: liveTicket.data.has_ticket };
        
        // Check past event tickets
        for (const event of pastEvents) {
          const res = await axios.get(`${API_URL}/api/events/ticket-status?event_key=past_${event.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          ticketMap[`past_${event.id}`] = res.data.has_ticket;
        }
        
        setTickets(ticketMap);
      } catch (err) {
        console.warn('Failed to check tickets:', err);
      } finally {
        setCheckingTickets(false);
      }
    };
    if (pastEvents.length > 0 || liveStream.is_live) {
      checkTickets();
    } else {
      setCheckingTickets(false);
    }
  }, [user, pastEvents, liveStream.is_live]);

  // Handle successful payment redirect
  useEffect(() => {
    const ticketStatus = searchParams.get('ticket');
    const eventKey = searchParams.get('event');
    const guestToken = searchParams.get('guest');
    if (ticketStatus === 'success' && eventKey) {
      if (guestToken) {
        // Guest purchase — keep the URL params so localStorage effect runs first
        toast.success("Ticket purchased! Check your phone — we've texted you a link to watch on any device. Save it!", { duration: 8000 });
      } else {
        toast.success('Ticket purchased successfully!');
      }
      setTickets(prev => ({ ...prev, [eventKey]: true }));
      // Clean URL slightly later so the guest-token effect captures it first
      setTimeout(() => window.history.replaceState({}, '', '/events'), 800);
    } else if (ticketStatus === 'cancel') {
      toast.error('Payment cancelled');
      window.history.replaceState({}, '', '/events');
    }
  }, [searchParams]);

  // Handle auto-purchase after login/signup redirect
  useEffect(() => {
    const purchaseParam = searchParams.get('purchase');
    if (purchaseParam && user && !checkingTickets) {
      // Clear the URL parameter
      window.history.replaceState({}, '', '/events');
      
      // Parse the purchase parameter (format: past_eventId or live_stream)
      if (purchaseParam === 'live_stream') {
        handlePurchaseTicket('live');
      } else if (purchaseParam.startsWith('past_')) {
        const eventId = purchaseParam.replace('past_', '');
        handlePurchaseTicket('past', eventId);
      }
    }
  }, [searchParams, user, checkingTickets]);

  // Handle guest ticket verification on success redirect
  useEffect(() => {
    const guestToken = searchParams.get('guest');
    const eventKey = searchParams.get('event');
    if (guestToken && eventKey) {
      // Store guest token in localStorage for this event
      const guestTickets = JSON.parse(localStorage.getItem('guestTickets') || '{}');
      guestTickets[eventKey] = guestToken;
      localStorage.setItem('guestTickets', JSON.stringify(guestTickets));
    }
  }, [searchParams]);

  const handlePurchaseTicket = async (eventType, eventId = null) => {
    if (!user) {
      toast.error('Please log in to purchase a ticket');
      return;
    }
    
    const eventKey = eventType === 'live' ? 'live_stream' : `past_${eventId}`;
    setPurchasingTicket(eventKey);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/events/purchase-ticket`, {
        origin_url: window.location.origin,
        event_type: eventType,
        event_id: eventId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.free) {
        toast.success('Free access granted!');
        setTickets(prev => ({ ...prev, [eventKey]: true }));
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error('Failed to purchase: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPurchasingTicket(null);
    }
  };

  const handleGuestPurchase = async (eventType, eventId = null) => {
    const eventKey = eventType === 'live' ? 'live_stream' : `past_${eventId}`;
    setPurchasingTicket(eventKey);
    
    try {
      const response = await axios.post(`${API_URL}/api/events/guest-purchase`, {
        origin_url: window.location.origin,
        event_type: eventType,
        event_id: eventId
      });
      
      if (response.data.requires_signup) {
        // Free events need signup
        toast.info('Free events require a quick signup');
        window.location.href = `/signup?redirect=/events&purchase=${eventKey}`;
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error('Failed to purchase: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPurchasingTicket(null);
    }
  };

  // Check if guest has access to an event
  const hasGuestAccess = (eventKey) => {
    // Live stream is subscribers-only — old guest tickets do NOT carry over
    if (eventKey === 'live_stream') return false;
    const guestTickets = JSON.parse(localStorage.getItem('guestTickets') || '{}');
    return !!guestTickets[eventKey];
  };

  // One-time cleanup: strip any cached live_stream guest token from previous purchases
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('guestTickets') || '{}');
      if (stored.live_stream) {
        delete stored.live_stream;
        localStorage.setItem('guestTickets', JSON.stringify(stored));
      }
    } catch { /* ignore */ }
  }, []);

  const getPrice = (event, isLive = false) => {
    if (isLive) {
      return isSubscriber ? liveStream.subscriber_price : liveStream.non_subscriber_price;
    }
    return isSubscriber ? event.subscriber_price : event.non_subscriber_price;
  };

  const canViewEvent = (eventKey, event = null) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'coach') return true;
    // Organizer-sponsored free live stream → everyone gets in
    if (eventKey === 'live_stream' && liveStream.free_for_all) {
      return true;
    }
    // Live stream + past event recordings: subscribers-only (no more pay-per-view)
    if (eventKey === 'live_stream' || eventKey.startsWith('past_')) {
      return isSubscriber;
    }
    if (tickets[eventKey]) return true;
    if (hasGuestAccess(eventKey)) return true;
    const price = event ? getPrice(event) : getPrice(null, true);
    return price === 0;
  };

  // Using pastEvents and upcomingEvents from state (fetched from API)

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-8 md:py-12 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight uppercase text-foreground leading-none mb-3">
              Clinics & Events
            </h1>
            <p className="font-serif text-xl text-muted-foreground leading-relaxed">
              Learn in person from Trent and other top-level ropers. Our clinics focus on real improvement—technical skills, competitive confidence, and practical knowledge you can use right away.
            </p>
          </div>
        </div>
      </section>

      {/* Live Stream Section - Shows when stream is live */}
      {liveStream.is_live && liveStream.embed_code && (
        <section className="py-8 bg-red-600/10 border-y-4 border-red-600">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-3">
                <Badge className="bg-red-600 animate-pulse flex items-center gap-2 text-lg px-4 py-2">
                  <Radio className="w-5 h-5" />
                  LIVE NOW
                </Badge>
                <h2 className="font-display font-bold text-2xl md:text-3xl uppercase">
                  {liveStream.title}
                </h2>
              </div>
              {liveStream.free_for_all ? (
                <Badge className="bg-green-600 text-white text-sm sm:text-lg px-3 sm:px-4 py-2" data-testid="free-event-badge">
                  Free Event — No Login Required
                </Badge>
              ) : (!isSubscriber && !canViewEvent('live_stream') && (
                <Link to="/subscription">
                  <Badge variant="secondary" className="text-sm sm:text-lg px-3 sm:px-4 py-2 cursor-pointer hover:bg-secondary/80 transition-colors" data-testid="subscribers-only-badge">
                    Subscribers Only
                  </Badge>
                </Link>
              ))}
            </div>
            
            {canViewEvent('live_stream') ? (
              <>
                {tickets['live_stream'] && getPrice(null, true) > 0 && (
                  <div className="flex items-center gap-2 text-green-600 mb-4">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">You have access to this event</span>
                  </div>
                )}
                <div className="rounded-xl overflow-hidden shadow-2xl bg-black">
                  {renderEmbed(liveStream.embed_code, liveStream.title)}
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <Card className="bg-background/95 backdrop-blur">
                  <CardContent className="p-8 text-center">
                    <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-display font-black text-2xl md:text-3xl uppercase mb-3 tracking-tight">
                      Subscribers Only
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      The live stream of "{liveStream.title}" is exclusive to active subscribers.
                      Subscribe to watch this and every future live event.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {!user ? (
                        <>
                          <Link to="/signup">
                            <Button size="lg" className="rounded-full px-8 font-bold uppercase tracking-wider" data-testid="signup-to-watch-btn">
                              Sign Up
                            </Button>
                          </Link>
                          <Link to="/login">
                            <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="login-to-watch-btn">
                              I Have an Account
                            </Button>
                          </Link>
                        </>
                      ) : (
                        <Link to="/subscription">
                          <Button size="lg" className="rounded-full px-8 font-bold uppercase tracking-wider" data-testid="subscribe-to-watch-btn">
                            Subscribe to Watch
                          </Button>
                        </Link>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-6">
                      Already a subscriber? Make sure you're signed in with your subscriber account.
                    </p>

                </CardContent>
              </Card>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Events */}
      <section className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-8">
            Events
          </h2>

          {upcomingEvents.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-16 text-center">
                <Calendar className="w-16 h-16 text-primary/40 mx-auto mb-6" />
                <h3 className="font-display font-bold text-2xl uppercase tracking-tight mb-3">
                  Stay Tuned!
                </h3>
                <p className="text-muted-foreground text-lg mb-2">
                  Keep an eye on this space for upcoming clinics and events.
                </p>
                <p className="text-sm text-muted-foreground">
                  Follow us on social media for the latest announcements!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {upcomingEvents.map(event => (
                <Card key={event.id} className="overflow-hidden">
                  <CardContent className="p-6 md:p-8">
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Event Details */}
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded capitalize">
                            {event.event_type?.replace('_', ' ') || 'Event'}
                          </span>
                        </div>
                        
                        <h3 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight mb-4">
                          {event.title}
                        </h3>
                        
                        <div className="grid sm:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-start gap-3">
                            <Calendar className="w-5 h-5 text-primary mt-0.5" />
                            <div>
                              <p className="font-semibold">
                                {event.event_date 
                                  ? new Date(event.event_date + 'T12:00:00').toLocaleDateString('en-US', { 
                                      weekday: 'long', 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric',
                                      timeZone: 'America/Edmonton'
                                    })
                                  : 'To Be Announced'}
                              </p>
                              {event.event_time && <p className="text-sm text-muted-foreground">{event.event_time}</p>}
                            </div>
                          </div>
                          
                          {event.location && (
                            <div className="flex items-start gap-3">
                              <MapPin className="w-5 h-5 text-primary mt-0.5" />
                              <div>
                                <p className="font-semibold">{event.location}</p>
                                <p className="text-sm text-muted-foreground">Location</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Full-width Map */}
                        {event.location && (
                          <FullWidthMap location={event.location} eventId={event.id} mapUrl={event.map_url} />
                        )}

                        {/* Description */}
                        {event.description && (
                          <p className="text-muted-foreground mb-6">{event.description}</p>
                        )}

                        {/* Spots Remaining */}
                        {event.capacity && (
                          <div className="mb-6" data-testid={`event-capacity-${event.id}`}>
                            <div className={`rounded-lg p-4 border ${event.capacity.spots_left === 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  {event.capacity.spots_left === 0 ? 'Event Full' : `${event.capacity.spots_left} Spot${event.capacity.spots_left !== 1 ? 's' : ''} Left`}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {event.capacity.total_count}/{event.capacity.max_total} registered
                                </span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2 mb-2">
                                <div 
                                  className={`h-2 rounded-full transition-all ${event.capacity.spots_left === 0 ? 'bg-red-500' : event.capacity.spots_left <= 5 ? 'bg-amber-500' : 'bg-green-500'}`}
                                  style={{ width: `${Math.min(100, (event.capacity.total_count / event.capacity.max_total) * 100)}%` }}
                                />
                              </div>
                              {event.capacity.categories && event.capacity.categories.length > 0 && (
                                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                  {event.capacity.categories.filter(c => c.max > 0).map(cat => (
                                    <span key={cat.name}>{cat.name}: {cat.count}/{cat.max}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Section */}
                      <div className="flex flex-col justify-center items-center gap-4 bg-muted/30 rounded-lg p-6">
                        {event.image_url && (
                          <img 
                            src={event.image_url} 
                            alt={event.title}
                            data-testid="event-image"
                            className="w-full rounded-lg mb-2"
                          />
                        )}
                        
                        {event.registration_url ? (
                          <a 
                            href={event.registration_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full"
                          >
                            <Button className="w-full" size="lg">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Register Now
                            </Button>
                          </a>
                        ) : !event.event_date ? (
                          // TBA events - show "I'm Interested" email button
                          <div className="text-center space-y-3">
                            <p className="text-sm text-muted-foreground">
                              Want to be notified when dates are announced?
                            </p>
                            <a 
                              href={`mailto:ryan@elevatedarena.com?subject=Interest in ${encodeURIComponent(event.title)}&body=Hi,%0D%0A%0D%0AI'm interested in the "${encodeURIComponent(event.title)}" event. Please let me know when dates and registration details are available.%0D%0A%0D%0AThank you!`}
                              className="w-full block"
                            >
                              <Button className="w-full" size="lg" variant="secondary">
                                <Mail className="w-4 h-4 mr-2" />
                                I'm Interested
                              </Button>
                            </a>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Past Event Recordings */}
      {pastEvents.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-8">
              Past Event Recordings
            </h2>
            <p className="text-muted-foreground mb-8">
              Missed a live event? Watch the recordings here.
            </p>

            {/* Selected Past Event Player */}
            {selectedPastEvent && canViewEvent(`past_${selectedPastEvent.id}`, selectedPastEvent) && (
              <Card className="mb-8 overflow-hidden border-2 border-primary">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-display uppercase">{selectedPastEvent.title}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedPastEvent(null)}>
                      Close Player
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="bg-black">
                    {renderEmbed(selectedPastEvent.embed_code, selectedPastEvent.title)}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {pastEvents.map(event => {
                const eventKey = `past_${event.id}`;
                const hasAccess = canViewEvent(eventKey, event);
                const price = getPrice(event);

                return (
                  <Card key={event.id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-1">
                            {event.title}
                          </h3>
                          {event.event_date && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(event.event_date + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Edmonton' })}
                            </p>
                          )}
                        </div>
                        {price > 0 && (
                          <Badge variant="secondary" className="text-xs px-3 py-1 uppercase tracking-wider">
                            <Lock className="w-3 h-3 mr-1" /> Subscribers
                          </Badge>
                        )}
                        {price === 0 && (
                          <Badge variant="secondary" className="text-xs px-3 py-1 uppercase tracking-wider">
                            <Lock className="w-3 h-3 mr-1" /> Subscribers
                          </Badge>
                        )}
                      </div>

                      {event.description && (
                        <p className="text-muted-foreground text-sm mb-4">{event.description}</p>
                      )}

                      {hasAccess ? (
                        <div className="space-y-3">
                          {(tickets[eventKey] || hasGuestAccess(eventKey)) && price > 0 && (
                            <div className="flex items-center gap-2 text-green-600 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              <span>You have access</span>
                            </div>
                          )}
                          <Button 
                            className="w-full"
                            onClick={() => setSelectedPastEvent(event)}
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Watch Recording
                          </Button>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-center space-y-3" data-testid={`past-event-locked-${event.id}`}>
                          <div className="flex items-center justify-center gap-2 text-primary">
                            <Lock className="w-4 h-4" />
                            <span className="font-display font-bold uppercase tracking-wider text-sm">Subscribers Only</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Past event recordings are exclusive to active subscribers.
                          </p>
                          {!user ? (
                            <div className="flex flex-col sm:flex-row gap-2 justify-center">
                              <Link to="/signup" className="flex-1">
                                <Button size="sm" className="w-full rounded-full font-bold uppercase tracking-wider" data-testid={`past-event-signup-${event.id}`}>
                                  Sign Up
                                </Button>
                              </Link>
                              <Link to="/login" className="flex-1">
                                <Button size="sm" variant="outline" className="w-full rounded-full" data-testid={`past-event-login-${event.id}`}>
                                  I Have an Account
                                </Button>
                              </Link>
                            </div>
                          ) : (
                            <Link to="/subscription">
                              <Button size="sm" className="rounded-full font-bold uppercase tracking-wider px-6" data-testid={`past-event-subscribe-${event.id}`}>
                                Subscribe to Watch
                              </Button>
                            </Link>
                          )}
                        </div>
                      )}
                      {/* Always-visible photo/video order links */}
                      <div className="mt-4 pt-4 border-t border-border flex gap-2 flex-wrap">
                        <Link to={`/gallery/${event.id}`} className="flex-1">
                          <Button variant="ghost" size="sm" className="w-full text-xs" data-testid={`event-photos-link-${event.id}`}>
                            📸 View / Buy Photos
                          </Button>
                        </Link>
                        <Link to={`/events/${event.id}/order-video`} className="flex-1">
                          <Button variant="ghost" size="sm" className="w-full text-xs" data-testid={`event-video-link-${event.id}`}>
                            🎬 Order Custom Video
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* What to Expect */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-8">
            What to Expect
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display font-bold uppercase tracking-wide mb-2">Small Groups</h3>
                <p className="text-muted-foreground text-sm">
                  Limited class sizes ensure you get personal attention and plenty of practice runs.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
                  <Video className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="font-display font-bold uppercase tracking-wide mb-2">Professional Filming</h3>
                <p className="text-muted-foreground text-sm">
                  Many clinics include professional video so you can review your progress afterwards.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-display font-bold uppercase tracking-wide mb-2">Full Day Instruction</h3>
                <p className="text-muted-foreground text-sm">
                  In-depth sessions covering fundamentals, technique, and competition strategy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Host an Event */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-4">
            Want to Host a Clinic?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            If you have an arena and want to bring an Elevated Roping Media clinic to your area, get in touch. We're always looking to connect with ropers across Western Canada.
          </p>
          <a href="tel:4036344306">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 font-bold uppercase tracking-wider"
            >
              <Phone className="w-5 h-5 mr-2" />
              Contact Us
            </Button>
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase mb-4">
            Can't Make It In Person?
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8">
            Get the same quality coaching online. Submit your videos and receive detailed feedback from Trent.
          </p>
          <Link to="/signup">
            <Button 
              size="lg" 
              className="bg-white hover:bg-white/90 text-primary rounded-full px-10 py-6 font-bold uppercase tracking-wider"
            >
              Try Online Coaching
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            <p className="text-background/60 text-xs">
              © {new Date().getFullYear()} Elevated Roping Media Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventsPage;
